/**
 * useCustomers Hook
 * React Query integration for customer management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { customerService } from '../services/customer.service';
import type {
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerListParams,
} from '../types/customer';
import { logger } from '../utils/logger';
import { isEnhancedError, getUserFriendlyMessage } from '../utils/errorDebugger';

const QUERY_KEYS = {
  customers: ['customers'] as const,
  customer: (id: string) => ['customers', id] as const,
  search: (query: string) => ['customers', 'search', query] as const,
};

interface UseCustomersOptions {
  initialPage?: number;
  initialLimit?: number;
  initialSearch?: string;
}

export function useCustomers(options: UseCustomersOptions = {}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(options.initialPage || 1);
  const [limit, setLimit] = useState(options.initialLimit || 12);
  const [search, setSearch] = useState(options.initialSearch || '');
  // Default sort: trucksDueWithin2Weeks descending (highest at top)
  const [sortBy, setSortBy] = useState<CustomerListParams['sortBy']>('trucksDueWithin2Weeks');
  const [sortOrder, setSortOrder] = useState<CustomerListParams['sortOrder']>('desc');

  // Fetch customers query
  const customersQuery = useQuery({
    queryKey: [...QUERY_KEYS.customers, { page, limit, search, sortBy, sortOrder }],
    queryFn: async () => {
      try {
        logger.debug('useCustomers: Fetching customers', {
          component: 'useCustomers',
          action: 'queryFn',
          params: { page, limit, search, sortBy, sortOrder },
        });

        const result = await customerService.getCustomers({ page, limit, search, sortBy, sortOrder });

        logger.debug('useCustomers: Fetch successful', {
          component: 'useCustomers',
          action: 'queryFn',
          count: result.customers.length,
          total: result.total,
          totalPages: result.totalPages,
        });

        return result;
      } catch (error) {
        // Log the error with context
        logger.error('useCustomers: Fetch failed', {
          component: 'useCustomers',
          action: 'queryFn',
          params: { page, limit, search, sortBy, sortOrder },
          error: error instanceof Error ? error.message : 'Unknown error',
          isEnhanced: isEnhancedError(error),
          correlationId: isEnhancedError(error) ? error.context?.correlationId : undefined,
        });

        // Display user-friendly error message
        const userMessage = getUserFriendlyMessage(error);
        logger.error(`User-facing error: ${userMessage}`);

        // Re-throw to let React Query handle the error state
        throw error;
      }
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (isEnhancedError(error) && error.context?.status) {
        const status = error.context.status;
        if (status >= 400 && status < 500) {
          logger.warn('Not retrying due to client error', {
            component: 'useCustomers',
            status,
            failureCount,
          });
          return false;
        }
      }

      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.min(1000 * Math.pow(2, attemptIndex), 4000);
      logger.debug('Retrying request', {
        component: 'useCustomers',
        attemptIndex,
        delay: `${delay}ms`,
      });
      return delay;
    },
  });

  // Create customer mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCustomerDto) => customerService.createCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customers });
    },
  });

  // Update customer mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerDto }) =>
      customerService.updateCustomer(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customers });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customer(variables.id) });
    },
  });

  // Delete customer mutation
  // SCHEMA MIGRATION: No force parameter needed - automatic vehicle cleanup
  const deleteMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      customerService.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customers });
    },
  });

  // Pagination handlers
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when changing limit
  }, []);

  // Search handler with debounce support
  const handleSearchChange = useCallback((query: string) => {
    setSearch(query);
    setPage(1); // Reset to first page when searching
  }, []);

  // Sort handlers
  const handleSortChange = useCallback((
    field: CustomerListParams['sortBy'],
    order: CustomerListParams['sortOrder']
  ) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  return {
    // Data
    customers: (customersQuery.data as any)?.customers || [],
    total: (customersQuery.data as any)?.total || 0,
    totalPages: (customersQuery.data as any)?.totalPages || 0,

    // State
    page,
    limit,
    search,
    sortBy,
    sortOrder,

    // Query state
    isLoading: customersQuery.isLoading,
    isError: customersQuery.isError,
    error: customersQuery.error,
    isFetching: customersQuery.isFetching,

    // Actions
    createCustomer: createMutation.mutateAsync,
    updateCustomer: updateMutation.mutateAsync,
    deleteCustomer: deleteMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Handlers
    setPage: handlePageChange,
    setLimit: handleLimitChange,
    setSearch: handleSearchChange,
    setSort: handleSortChange,

    // Utilities
    refetch: customersQuery.refetch,
  };
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.customer(id),
    queryFn: () => customerService.getCustomer(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch own customer profile (for customer role users)
 */
export function useOwnProfile() {
  return useQuery({
    queryKey: ['customer', 'me'],
    queryFn: () => customerService.getOwnProfile(true),
    staleTime: 30000,
  });
}

export function useCustomerSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.search(query),
    queryFn: () => customerService.searchCustomers(query),
    enabled: enabled && query.length > 0,
    staleTime: 10000,
  });
}
